import React, { FC } from "react";

import { colorType, orientationType } from "lib/types";

export interface TextBoxProps {
  /**
   * The aria-label attribute for the TextBox.
   */
  ariaLabel?: string;
  /**
   * The content of the TextBox.
   */
  children: React.ReactNode;
  /**
   * Additional CSS class for the TextBox.
   */
  className?: string;
  /**
   * The color of the TextBox.
   */
  color?: colorType | "transparent" | "warning";
  /**
   * The component to be used for rendering the TextBox.
   */
  component?: React.ElementType;
  /**
   * Disables the margin of the TextBox.
   */
  disableMargin?: boolean;
  /**
   * Disables the padding of the TextBox.
   */
  disablePadding?: boolean;
  /**
   * Determines if the TextBox should have a lighter dark mode.
   */
  lighterDarkMode?: boolean;
  /**
   * Limits the width of the children inside the TextBox.
   */
  limitChildrenWidth?: boolean;
  /**
   * Determines if the TextBox should be open by default. Only works with TextBoxExpandable.
   */
  open?: boolean;
  /**
   * The orientation of the TextBox.
   */
  orientation?: orientationType;
  /**
   * The role attribute for the TextBox.
   */
  role?: string;
  /**
   * The size of the TextBox.
   */
  size?: "regular" | "large" | "full";
  /**
   * Determines if the TextBox should use prose style.
   */
  useProse?: boolean;
}

export const TextBox: FC<TextBoxProps> = ({
  ariaLabel,
  children,
  className,
  color = "beige",
  component = "div",
  disableMargin = false,
  disablePadding = false,
  lighterDarkMode = false,
  limitChildrenWidth = false,
  open = false,
  orientation = "center",
  role,
  size = "regular",
  useProse = false,
}) => {
  const getOrientation = {
    left: "lg:ml-auto",
    right: "lg:mr-auto",
    center: "lg:mx-auto",
  }[orientation];

  const getColor = {
    blue: "bg-lightblue",
    beige: "bg-beige",
    yellow: "bg-yellow",
    red: "bg-red",
    white: "bg-white",
    green: "bg-green",
    warning: "bg-beige dark:bg-warningDark",
    transparent: "bg-transparent dark:bg-transparent",
    brown: "bg-warningDark",
  }[color];

  const getSize = {
    full: "lg:max-w-[none]",
    regular: "lg:max-w-xl",
    large: "lg:max-w-screen-lg",
  }[size];

  const getPadding = disablePadding ? "p-0" : `px-4 sm:px-6 py-8 md:p-12`;

  const getProseStyles = () => {
    if (useProse)
      return "prose prose-lg prose-neutral dark:prose-invert prose-p:leading-[1.75]";
    return "space-y-3";
  };

  const Wrapper = component;

  return (
    <Wrapper
      aria-label={ariaLabel}
      className={`more-contrast-border relative z-20 ${
        limitChildrenWidth ? "[&>p]:max-w-[60ch]" : ""
      } ${getProseStyles()} break-words rounded-t-2xl rounded-bl-2xl ${getPadding} text-xl ${
        lighterDarkMode ? "dark:bg-slate-700" : "dark:bg-slate-800"
      } ${getSize} ${getColor} ${disableMargin ? "" : getOrientation} ${className || ""}`}
      open={open}
      role={role}
    >
      {children}
    </Wrapper>
  );
};

export default TextBox;
